import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styles from './RelatoriosV2.module.css';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtDateBR = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Helpers de período
const isoLocal = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const hoje = () => isoLocal(new Date());
const ontemISO = () => isoLocal(new Date(Date.now() - 86400000));
const inicioMesAtual = () => {
  const d = new Date();
  return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1));
};
const inicioMesPassado = () => {
  const d = new Date();
  return isoLocal(new Date(d.getFullYear(), d.getMonth() - 1, 1));
};
const fimMesPassado = () => {
  const d = new Date();
  return isoLocal(new Date(d.getFullYear(), d.getMonth(), 0));
};
const inicioSemana = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay()); // domingo
  return isoLocal(d);
};
const inicioAno = () => isoLocal(new Date(new Date().getFullYear(), 0, 1));
const daysAgo = (n) => isoLocal(new Date(Date.now() - n * 86400000));

const PRESETS = [
  { key: 'hoje',      label: 'Hoje',           rangeFn: () => [hoje(), hoje()] },
  { key: 'ontem',     label: 'Ontem',          rangeFn: () => [ontemISO(), ontemISO()] },
  { key: 'semana',    label: 'Esta semana',    rangeFn: () => [inicioSemana(), hoje()] },
  { key: 'mes',       label: 'Este mês',       rangeFn: () => [inicioMesAtual(), hoje()] },
  { key: 'mes_pass',  label: 'Mês passado',    rangeFn: () => [inicioMesPassado(), fimMesPassado()] },
  { key: '30d',       label: 'Últimos 30 dias',rangeFn: () => [daysAgo(29), hoje()] },
  { key: '90d',       label: 'Últimos 90 dias',rangeFn: () => [daysAgo(89), hoje()] },
  { key: 'ano',       label: 'Este ano',       rangeFn: () => [inicioAno(), hoje()] },
];

const LOJA_TAGS = {
  ecommerce: { tag: 'E-commerce', tagClass: 'tagEcommerce', cardClass: 'ecommerce' },
  centro:    { tag: 'Centro',     tagClass: 'tagCentro',    cardClass: 'centro' },
  caxias:    { tag: 'Caxias',     tagClass: 'tagCaxias',    cardClass: 'caxias' },
};

// Mensagem em linguagem simples quando os dados da loja estão atrasados
// (import da física pode atrasar — ver backend cache_status). Sem jargão técnico.
const mensagemDefasagem = (cacheStatus) => {
  if (!cacheStatus?.defasado) return null;
  if (!cacheStatus.ultima_venda_disponivel) {
    return 'Ainda não há vendas recentes disponíveis para esta loja.';
  }
  return `Dados até ${fmtDateBR(cacheStatus.ultima_venda_disponivel)} — atualização atrasada, os últimos dias ainda não aparecem aqui.`;
};

const Consolidado = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [presetAtivo, setPresetAtivo] = useState('mes');
  const [dataInicio, setDataInicio] = useState(inicioMesAtual());
  const [dataFim, setDataFim] = useState(hoje());

  const carregar = useCallback(async (di, df) => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get('/api/relatorios-faturamento-v2/consolidado', {
        params: { data_inicio: di, data_fim: df },
      });
      setData(resp);
    } catch (err) {
      console.error('Erro consolidado:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar(dataInicio, dataFim);
  }, [carregar, dataInicio, dataFim]);

  const escolherPreset = (preset) => {
    setPresetAtivo(preset.key);
    const [di, df] = preset.rangeFn();
    setDataInicio(di);
    setDataFim(df);
  };

  const onChangeData = (campo, valor) => {
    setPresetAtivo('custom');
    if (campo === 'inicio') setDataInicio(valor);
    else setDataFim(valor);
  };

  if (!data?.success && !loading) return <div className={styles.container}><div className={styles.loading}>Erro ao carregar dados.</div></div>;

  const periodoLabel = PRESETS.find(p => p.key === presetAtivo)?.label
    || `${fmtDateBR(dataInicio)} a ${fmtDateBR(dataFim)}`;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Relatórios Faturamento</h1>
          <div className={styles.subtitle}>
            Visão consolidada · <strong>{periodoLabel}</strong> · {fmtDateBR(dataInicio)} a {fmtDateBR(dataFim)}
          </div>
        </div>
      </div>

      {/* Filtro de período */}
      <div className={styles.periodFilter}>
        <div className={styles.presetsRow}>
          {PRESETS.map(p => (
            <button
              key={p.key}
              className={`${styles.presetBtn} ${presetAtivo === p.key ? styles.presetActive : ''}`}
              onClick={() => escolherPreset(p)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className={styles.customRange}>
          <label>De
            <input type="date" value={dataInicio} max={dataFim} onChange={(e) => onChangeData('inicio', e.target.value)} />
          </label>
          <label>Até
            <input type="date" value={dataFim} min={dataInicio} max={hoje()} onChange={(e) => onChangeData('fim', e.target.value)} />
          </label>
        </div>
      </div>

      {loading && <div className={styles.loading}>Atualizando...</div>}

      {!data?.success ? null : <>

      {/* Totais (3 lojas somadas) */}
      <div className={styles.totaisRow}>
        <div className={styles.totalCard}>
          <div className={styles.totalLabel}>Faturamento do Período</div>
          <div className={styles.totalValue}>{fmtBRL(data.total.faturamento_mes)}</div>
          <div className={styles.totalSub}>{periodoLabel.toLowerCase()} · 3 lojas</div>
        </div>
        <div className={styles.totalCard} style={{ background: 'linear-gradient(135deg, #2ECC71, #27AE60)' }}>
          <div className={styles.totalLabel}>Vendas Ontem</div>
          <div className={styles.totalValue}>{fmtBRL(data.total.faturamento_ontem)}</div>
          <div className={styles.totalSub}>encerrado</div>
        </div>
        <div className={styles.totalCard} style={{ background: 'linear-gradient(135deg, #3498DB, #2980B9)' }}>
          <div className={styles.totalLabel}>Vendas Hoje</div>
          <div className={styles.totalValue}>{fmtBRL(data.total.faturamento_hoje)}</div>
          <div className={styles.totalSub}>parcial em tempo real</div>
        </div>
        <div className={styles.totalCard} style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}>
          <div className={styles.totalLabel}>Produtos Vendidos</div>
          <div className={styles.totalValue}>{fmtNum(data.total.qtde_itens_mes)}</div>
          <div className={styles.totalSub}>unidades no período</div>
        </div>
        <div className={styles.totalCard} style={{ background: 'linear-gradient(135deg, #E74C3C, #C0392B)' }}>
          <div className={styles.totalLabel}>Descontos Concedidos</div>
          <div className={styles.totalValue}>{fmtBRL(data.total.descontos_mes)}</div>
          <div className={styles.totalSub}>no período</div>
        </div>
      </div>

      {/* Cards por loja */}
      <div className={styles.lojaCardsGrid}>
        {data.lojas.map((l) => {
          const meta = LOJA_TAGS[l.loja] || {};
          return (
            <div key={l.loja} className={`${styles.lojaCard} ${styles[meta.cardClass] || ''}`}>
              <div className={styles.lojaHeader}>
                <h3 className={styles.lojaTitle}>{l.label}</h3>
                <span className={`${styles.lojaTag} ${styles[meta.tagClass] || ''}`}>{meta.tag}</span>
              </div>

              {mensagemDefasagem(l.cache_status) && (
                <div className={styles.defasagemAlert}>⚠️ {mensagemDefasagem(l.cache_status)}</div>
              )}

              <div className={styles.kpiRow}>
                <div className={styles.kpiBox}>
                  <div className={styles.kpiLabel}>Faturamento período</div>
                  <div className={`${styles.kpiValue} ${styles.big}`}>{fmtBRL(l.kpis.mes.faturamento)}</div>
                  <div className={styles.kpiSub}>{fmtNum(l.kpis.mes.num_vendas)} vendas</div>
                </div>
                <div className={styles.kpiBox}>
                  <div className={styles.kpiLabel}>Ticket médio</div>
                  <div className={styles.kpiValue}>{fmtBRL(l.kpis.mes.ticket_medio)}</div>
                  <div className={styles.kpiSub}>{fmtNum(l.kpis.mes.qtde_itens)} itens</div>
                </div>
              </div>

              <div className={styles.kpiRow}>
                <div className={styles.kpiBox}>
                  <div className={styles.kpiLabel}>Ontem</div>
                  <div className={styles.kpiValue}>{fmtBRL(l.kpis.ontem.faturamento)}</div>
                </div>
                <div className={styles.kpiBox}>
                  <div className={styles.kpiLabel}>Hoje (parcial)</div>
                  <div className={styles.kpiValue}>{fmtBRL(l.kpis.hoje.faturamento)}</div>
                </div>
              </div>

              {l.kpis.mes.descontos > 0 && (
                <div className={styles.kpiBox}>
                  <div className={styles.kpiLabel}>Descontos</div>
                  <div className={styles.kpiValue}>{fmtBRL(l.kpis.mes.descontos)} · {l.kpis.mes.percentual_desconto.toFixed(1)}%</div>
                </div>
              )}

              <div className={styles.topProdMini}>
                <h4>Top 5 produtos do período</h4>
                <ol>
                  {l.top_5_produtos.length === 0 ? (
                    <li><span className={styles.empty}>Sem dados</span></li>
                  ) : l.top_5_produtos.map((p, i) => (
                    <li key={p.codigo}>
                      <span className={styles.topProdNome}>
                        <strong style={{ marginRight: 6 }}>{i + 1}.</strong>
                        {p.descricao}
                      </span>
                      <span className={styles.topProdQtd}>{fmtNum(p.qtde)}u</span>
                    </li>
                  ))}
                </ol>
              </div>

              <Link to={`/relatorios-faturamento-v2/${l.loja}`} className={styles.detalheBtn}>
                Ver detalhes da loja →
              </Link>
            </div>
          );
        })}
      </div>

      </>}
    </div>
  );
};

export default Consolidado;
